<template>
  <div ref="wrapper" class="nx-editor">
    <div ref="editor"></div>

    <nx-quill-spelling-check-tool v-if="hasSpellingCheck"
                                  :field="name"
                                  :field-label="label"
                                  :quill="quill"
                                  :force-spellcheck-before-save="forceSpellcheckBeforeSave"
                                  :force-spellcheck-before-publish="forceSpellcheckBeforePublish"/>

    <component
      v-if="blotDialog"
      v-bind="blotDialog.props"
      @close="blotDialog = null"
      :is="blotDialog.component"
      :quill="quill"
    ></component>

    <component
      :active="dialog.active"
      :blot="dialog.blot"
      :is="dialog.component"
      :quill="quill"
      @dismiss="onDismiss"
      v-bind="{ ...dialog.props, ...dialog.fields }"
      v-if="dialog.component"
    ></component>

    <nx-fullscreen-dialog
      v-if="!modal"
      :active="fullscreen"
      :title="label"
      @dismiss="fullscreen = false"
    >
      <template slot="toolbar-items">
        <v-btn
          @click.native="fullscreen = false"
          color="accent"
          dark flat
          v-html="$t('message.common.close')"
        ></v-btn>
      </template>

      <v-container class="editor-modal">
        <component
          :is="editorName"
          ref="modalEditor"
          modal
          :disabled="disabled"
          :hint="hint"
          :options="options"
          :type="type"
          :value="value"
          :name="name"
          :label="label"
          :force-spellcheck-before-save="forceSpellcheckBeforeSave"
          :force-spellcheck-before-publish="forceSpellcheckBeforePublish"
          @input="handleFullscreenInput"
        />
        <div :class="{ ...counterClass, counter: true }">{{ counter }}</div>
      </v-container>
    </nx-fullscreen-dialog>

  </div>
</template>

<script>
import uuidV4 from 'uuid/v4';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import { debounce, flattenDeep, isEqual } from 'lodash';
import get from 'lodash/get';
import QuillEventBus, { QuillEvents } from '@/components/texteditor/event-bus';

import Quill from 'quill';
import defaultOptions from '@/components/texteditor/config/options';
import { getScrollableParent } from '@/utils';
import throttle from 'lodash/throttle';
import NxQuillSpellingCheckTool from '@/components/texteditor/spelling-check/SpellingCheckTool';
import NxFullscreenDialog from '@/components/dialogs/FullscreenDialog';
import { mapState } from 'vuex';
import { i18n } from '@/plugins/vue-i18n';

const Delta = Quill.import('delta');


/**
 * QuillEditor Component
 *
 * @url https://quilljs.com
 *
 * @property {String, Object}  [value]    Text Editor content (Plain HTML or Deltas)
 * @property {String}          type       Change Text Editor type (basic, advanced)
 * @property {Object}          [options]  Add more options to the Text Editor
 * @property {String}          hint       Change the default Text Editor placeholder
 * @property {Boolean}         disabled   Disable Text Editor
 *
 * @fires QuillEditor#blur                Emit Deltas Array
 * @fires QuillEditor#focus
 * @fires QuillEditor#length              Emit Text length
 * @fires QuillEditor#input               Emit Deltas Array
 */

const channelToolbarHeight = 44;
const scrollEditorHeightLimit = 200;
const undoSvg = `<svg viewBox="0 0 20.47 9">
    <path class="ql-fill" d="M10.5,1A10.46,10.46,0,0,0,3.6,3.6L0,0V9H9L5.38,5.38A8,8,0,0,1,18.1,9l2.37-.78A10.51,10.51,0,0,0,10.5,1Z"/>
  </svg>`;

const redoSvg = `<svg viewBox="0 0 20.46 9">
    <path class="ql-fill" d="M17.4,3.6A10.47,10.47,0,0,0,.54,8.22L2.9,9A8,8,0,0,1,15.62,5.38L12,9h9V0Z" transform="translate(-0.54)"/>
  </svg>`;

export default {
  name: 'nx-quill-editor',
  components: {

    NxFullscreenDialog,
    NxQuillSpellingCheckTool,
  },
  props: {
    value: [String, Array],
    name: String,
    label: String,
    type: String,
    options: Object,
    maxHeight: Number,
    hint: String,
    disabled: Boolean,
    modal: Boolean,
    forceSpellcheckBeforeSave: Boolean,
    forceSpellcheckBeforePublish: Boolean,
    counter: [String, Number],
    counterClass: Object,
    editorName: {
      type: String,
      default: 'nx-quill-editor',
    },
  },
  data() {
    return {
      dialog: {
        active: false,
        component: null,
        blot: null,
        fields: {},
      },
      sidebar: {
        visible: false,
        value: false,
        top: 0,
        epsilon: 14,
        buttons: this.options,
      },
      blotDialog: null,
      toolbar: null,
      scrollableParent: null,
      initialized: false,
      fullscreen: false,
    };
  },
  computed: {
    textLength() {
      if (!this.quill) return 0;
      // If Quill is empty, it's return '\n'
      // Then the text length is almost 1
      return this.quill.getLength() - 1;
    },
    computedOptions() {
      const options = Object.assign(defaultOptions[this.type], this.options || {});

      if (this.hint) {
        options.placeholder = this.hint;
      }

      return options;
    },
    hasSpellingCheck() {
      if (!this.initialized) {
        return false;
      }
      return flattenDeep(this.computedOptions.modules.toolbar.container)
        .includes('grammar-checker');
    },
    ...mapState({
      currentLocale: state => state.locale.current,
    }),
  },
  methods: {
    onBlotSelected({
      uuid,
      params,
    }) {
      if (this.quill.uuid !== uuid) {
        return;
      }

      this.blotDialog = params;
    },
    addUndoRedoButtons(currentTry = 1) {
      const undoBtn = this.$el.querySelector('.ql-undo');
      const redoBtn = this.$el.querySelector('.ql-redo');

      if (!undoBtn && !redoBtn && currentTry <= 5) { // Give some time so the toolbar is initialized
        this.$nextTick(this.addUndoRedoButtons.bind(this, currentTry + 1));
        return;
      }

      if (undoBtn) {
        undoBtn.innerHTML = undoSvg;
      }
      if (redoBtn) {
        redoBtn.innerHTML = redoSvg;
      }
    },
    hooks() {
      // By default no hooks needed
    },
    setContent(value) {
      if (!this.quill) return;
      if (!value) return;

      // Save the current cursor index before setting the new content
      const currentIndex = this.quill.getSelection();
      // this.quill.setContents(...) resets the cursor index to 0
      this.quill.setContents(value, Quill.sources.SILENT);
      if (currentIndex) {
        this.quill.setSelection(currentIndex.index);
      }

      this.quill.history.clear();
      this.$emit('length', this.textLength);
      this.$emit('input:as-text', this.quill.getText());
    },
    getContent() {
      const delta = this.quill.getContents();

      delta.ops.forEach((op) => {
        if (op.attributes && op.attributes.searchedString) {
          delete op.attributes.searchedString;
        }
        return op;
      });

      if (delta.ops.length === 1
        && isEqual(delta.ops[0], { insert: '\n' })) {
        // Empty content
        return [];
      }

      return delta.compose(new Delta()).ops;
    },
    fixToolbar() {
      if (!this.toolbar.container.classList.contains('affix')) {
        this.toolbar.container.style.width = `${this.$refs.wrapper.clientWidth}px`;
      }

      if (this.isSticky()) {
        this.toolbar.container.classList.add('affix');
        this.$refs.wrapper.style.paddingTop = `${this.toolbar.container.clientHeight}px`;
        this.affixToolbarToMenu();
      } else {
        this.$refs.wrapper.style.paddingTop = 0;
        this.toolbar.container.classList.remove('affix');
        this.toolbar.container.style.transform = null;
      }
    },
    onDismiss() {
      this.active = false;
      this.quill.enable(true);
      this.dialog = {
        active: false,
        component: null,
        blot: null,
        fields: {},
        props: {},
      };
    },
    isSticky() {
      const wrapperBound = this.$refs.wrapper.getBoundingClientRect();

      return (
        wrapperBound.height > window.outerHeight
        && wrapperBound.top < channelToolbarHeight
        && wrapperBound.top + wrapperBound.height
        > channelToolbarHeight + scrollEditorHeightLimit
      );
    },
    affixToolbarToMenu() {
      if (this.modal) return;
      const menu = document.getElementById('nx-editor-header');
      if (!menu) return;
      if (!this.toolbar) return;
      if (!this.toolbar.container.classList.contains('affix')) return;
      const transformMap = {
        'translateY(0px)': 'translateY(160px)',
        'translateY(-64px)': 'translateY(96px)',
      };
      this.toolbar.container.style.transform = transformMap[menu.style.transform];
    },
    reactToScrollableParentEvents() {
      this.scrollableParent = getScrollableParent(this.$el);
      this.throttleScroll = throttle(this.fixToolbar, 50);
      this.scrollableParent.addEventListener('scroll', this.throttleScroll, { passive: true });
    },
    reactToContentMenuTransforms() {
      if (this.modal) return;
      const menu = document.getElementById('nx-editor-header');
      if (!menu) return;
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'style') {
            this.affixToolbarToMenu();
          }
        });
      });
      observer.observe(menu, {
        attributes: true,
        attributeFilter: ['style'],
      });
    },
    onShowCreateBlot({
      uuid,
      params,
    }) {
      if (this.quill.uuid !== uuid) {
        return;
      }

      const {
        dialogConfig,
        fields,
      } = params;
      this.openButtonDialog(dialogConfig, null, fields);
    },
    openButtonDialog(button, blot = null, fields = {}) {
      this.dialog = {
        active: false,
        blot,
        component: button.component,
        fields,
        props: button.props || {},
      };
      this.$nextTick(() => {
        this.dialog.active = true;
      });
    },
    handleFullscreenInput(value) {
      this.setContent(value);
      this.$emit('blur', this.getContent());
      this.$emit('input', this.getContent());
      this.$emit('length', this.textLength);
      this.$emit('input:as-text', this.quill.getText());
    },
    onFullscreen({ uuid }) {
      if (this.quill.uuid !== uuid) {
        return;
      }
      this.fullscreen = true;
    },
  },
  watch: {
    value(value) {
      if (!isEqual(this.getContent(), value)) {
        this.setContent(value);
      }
    },
    disabled(value) {
      this.quill.enable(!value);
    },
    hasSpellingCheck(value) {
      if (value) {
        // disable browser native spellcheck
        this.quill.root.setAttribute('spellcheck', false);
      }
    },
    currentLocale: {
      handler(locale) {
        const quillVariables = get(i18n, `messages.${locale}.message.editor.quillVariables`, null);
        if (quillVariables) {
          Object.keys(quillVariables).forEach((cssVariable) => {
            document.documentElement.style.setProperty(cssVariable, quillVariables[cssVariable]);
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.quill = new Quill(this.$refs.editor, this.computedOptions);
    this.quill.uuid = uuidV4();
    this.toolbar = this.quill.getModule('toolbar');

    this.setContent(this.value);

    this.hooks();
    this.addUndoRedoButtons();

    // Events
    this.quill.on(Quill.events.SELECTION_CHANGE, (range) => {
      if (!range) {
        if (this.$refs.editor) {
          this.$emit('blur', this.getContent());
          this.$emit('focus', false);
        }
      } else {
        this.$emit('focus', true);
      }
    });

    // Update model if text changes
    // eslint-disable-next-line
    this.quill.on(Quill.events.TEXT_CHANGE, debounce(() => {
      this.$emit('blur', this.getContent());
      this.$emit('input', this.getContent());
      this.$emit('length', this.textLength);
      this.$emit('input:as-text', this.quill.getText());
    }, 600));
    QuillEventBus.$on(QuillEvents.CREATE, this.onShowCreateBlot);
    QuillEventBus.$on(QuillEvents.BLOT_SELECTED, this.onBlotSelected);
    QuillEventBus.$on(QuillEvents.FULLSCREEN, this.onFullscreen);
    // Listen to scrollable parent events
    this.reactToScrollableParentEvents();

    // Listen to menu transforms
    this.reactToContentMenuTransforms();

    // Emit ready event
    this.$emit('ready', this.quill);
    this.initialized = true;
  },
  beforeDestroy() {
    QuillEventBus.$off(QuillEvents.BLOT_SELECTED, this.onShowCreateBlot);
    QuillEventBus.$off(QuillEvents.CREATE, this.onBlotSelected);
    QuillEventBus.$off(QuillEvents.FULLSCREEN);
    this.scrollableParent.removeEventListener('scroll', this.throttleScroll);
  },
};
</script>

<style lang="stylus">
// base
$channelToolbar_height = 48px;
$epsilon = 4px;
$elevation3 = 0 2px 4px -1px rgba(#000, 0.2), 0 4px 5px 0 rgba(#000, 0.14), 0 1px 10px 0 rgba(#000, 0.12);
$quill_fontSize = 16px;
$quill_lineHeight = 1.75;
$quill_fontFamily = 'Roboto', sans-serif;
$quill_bgColor = #F7F7FA;
$quill_fgColor = #27272E;
$quillButton_fgColor = #676775;
$quillButton_fgColor_hover = #000;
$quillButton_fgColor_active = #4ed19c;
$quillButtonAffix_fgColor = darken(#fff, 20%);
$quillButtonAffix_fgColor_hover = #fff;

// style
.nx-editor {
  display: block;
  width: 100%;
  position: relative;

  .check-spelling {
    height: 100%;
    position: absolute;
    top: 40px;
    right: -40px;
  }

  .check-spelling__badge {
    position: sticky;
    right: 0;
    top: 120px;
  }

  .check-spelling__badge.v-badge--overlap.v-badge--bottom {
    .v-badge__badge {
      bottom: 0px;
      right: 6px;
      border: 2px solid white !important;

      .v-avatar {
        height: 20px !important;
        min-width: 19px;
      }
    }
  }

  .ql-tooltip.spell-check-tooltip {
    z-index: 1;

    & aside {
      position: relative;

      & button {
        position: absolute;
        top: 0;
        right: 0;
      }

      & h3 {
        max-width: calc(100% - 10px);
        white-space: break-spaces;
      }
    }

    &::before {
      content: unset !important;
    }

    ul.replacement-list {
      list-style: none;
      padding-left: unset;
      margin-top: 4px;

      & li {
        display: inline-block;
        cursor: pointer;
        border: 1px solid grey;
        padding: 4px;
        margin-right: 8px;
        background: #239aff;
        border-radius: 4px;
        color: white;
        font-size: 16px;
        font-weight: 600;

        &:hover {
          background-color: #1976f0;
        }

        &:last-child {
          margin-right: unset;
        }
      }
    }
  }

  .editor-modal {
    background-color: $nx-primary--lighten-5;

    .counter {
      margin-top: 18px;
      color: $nx-primary--darken-1;

      &.input-group__counter--soft-limit-reached {
        color: $warning;
      }

      &.input-group__counter--recommended-min-reached {
        color: $success
      }
    }

    .ql-toolbar {
      background-color: $nx-primary--lighten-5;
      position: fixed;

      &.affix {
        top: 24px;
      }
    }

    .ql-container {
      padding-top: 42px;
    }
  }

  .ql-searched-string {
    background-color: #f8ff00;
    display: inline-block;

    &[data-selected] {
      background-color: #ff7700;
    }
  }

  .ql-snow {
    &.ql-toolbar, &.ql-toolbar .ql-picker-label, &.ql-container {
      border: none;
    }

    &.ql-toolbar {
      padding: 10px 7px 8px;

      .ql-picker-label {
        border: none;
      }
    }

    .ql-editor {
      padding: 10px 15px 15px;

      .toolbar {
        white-space: normal;
      }

      .ql-embed {
        margin: 0 auto 28px;
        display: inline-flex;
        width: 100%;
      }
    }

    .ql-tooltip::before {
      content: "URL:"
    }

    .ql-tooltip .ql-action::after {
      content: "Editar"
    }


    .ql-tooltip .ql-remove::before {
      content: "Eliminar"
    }

    .ql-tooltip.ql-editing[data-mode="link"]::before {
      content: "Introduce el link:";
    }

    .ql-tooltip.ql-editing .ql-action::after {
      content: "Guardar"
    }
  }

  .ql-toolbar {
    padding: 10px;
    font-family: $quill_fontFamily;
    z-index: 1;

    &.affix {
      top: 6px;
      position: fixed;
      background-color: #27272E;
      border-radius: 2px;
      box-shadow: $elevation3;
      opacity: 0.95;
      transform: translateY($channelToolbar_height + $epsilon);
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      .ql-picker-label {
        color: $quillButtonAffix_fgColor;

        .ql-stroke {
          stroke: $quillButtonAffix_fgColor;
        }

        &:hover {
          color: $quillButtonAffix_fgColor_hover;

          .ql-stroke {
            stroke: $quillButtonAffix_fgColor_hover;
          }
        }
      }

      button {
        .ql-stroke {
          stroke: $quillButtonAffix_fgColor;
        }

        .ql-fill, i {
          fill: $quillButtonAffix_fgColor;
          color: $quillButtonAffix_fgColor;
        }

        &:hover {
          .ql-stroke {
            stroke: $quillButtonAffix_fgColor_hover;
          }

          .ql-fill, i {
            fill: $quillButtonAffix_fgColor_hover;
            color: $quillButtonAffix_fgColor_hover;
          }
        }
      }
    }

    .ql-picker-label {
      color: $quillButton_fgColor;

      .ql-stroke {
        stroke: $quillButton_fgColor;
      }

      &:hover {
        color: $quillButton_fgColor_hover;

        .ql-stroke {
          stroke: $quillButton_fgColor_hover;
        }
      }
    }

    .ql-picker-item {
      color: $quillButton_fgColor;
      border-color: $quillButton_fgColor;

      &:hover {
        color: $quillButton_fgColor_hover;
      }
    }

    button {
      .ql-stroke {
        stroke: $quillButton_fgColor;
      }

      .ql-fill, i {
        fill: $quillButton_fgColor;
        color: $quillButton_fgColor;
      }

      &:hover {
        .ql-stroke {
          stroke: $quillButton_fgColor_hover;
        }

        .ql-fill, i {
          fill: $quillButton_fgColor_hover;
          color: $quillButton_fgColor_hover;
        }
      }

      &.ql-active {
        .ql-stroke {
          stroke: $quillButton_fgColor_active;
        }

        .ql-fill, i {
          fill: $quillButton_fgColor_active;
          color: $quillButton_fgColor_active;
        }
      }

      &.ql-undo svg, &.ql-redo svg {
        width: 100%;
      }
    }
  }

  .ql-container {
    color: $quill_fgColor;
    min-height: 150px;
    position: relative;
  }

  .ql-editor {
    font-family: $quill_fontFamily;
    font-size: $quill_fontSize;
    line-height: $quill_lineHeight;

    > :not(:last-child) {
      margin-bottom: 8px;
    }
  }

  .ql-clipboard {
    height: 100%;
    width: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: -1;
  }
}
</style>
